import React from 'react';
import { BLOCK_TYPE_KEYS } from '@/lib/constants';
import { useDispatch, useShallowSelector } from '@/lib/reduxHooks';
import * as actions from '../../store/senderBuild/actions';

const INVALID_KEYS = [
  'Tab',
  'CapsLock',
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Enter',
  'Backspace',
  'Delete',
  'Escape',
];

export function useEnableTextBlockEditingKeyListener() {
  const dispatch = useDispatch();
  const state = useShallowSelector((state) => ({
    selectedBlocks: state.senderBuild.selectedBlocks,
    blocks: state.senderBuild.blocks,
    copyCache: state.senderBuild.copyCache,
    currentDocument: state.senderBuild.currentDocument,
    currentPage: state.senderBuild.currentPage,
    blockGroups: state.senderBuild.groups,
    pageDimensions: state.senderBuild.pageDimensions,
    inputFocused: state.senderBuild.inputFocused,
    isTextBlockEditingEnabled: state.senderBuild.isTextBlockEditingEnabled,
  }));

  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const charCode = e.key ? e.key.toLowerCase() : '';
      const { inputFocused } = state;
      if (state.selectedBlocks.blockIndices.length > 0 && !inputFocused) {
        const copyCommand = (e.ctrlKey && charCode === 'c') || (e.metaKey && charCode === 'c');
        const selectedPageBlocks = state.blocks[state.selectedBlocks.documentId!][state.selectedBlocks.pageIndex!];
        if (copyCommand && !state.isTextBlockEditingEnabled) {
          const copiedBlocks = state.selectedBlocks.blockIndices.map((i) => selectedPageBlocks[i]);
          dispatch(actions.copyBlocksToCache(copiedBlocks));
        }
      }

      if (!state.selectedBlocks.blockIndices.length) return;

      const block =
        state.blocks[state.selectedBlocks.documentId!][state.selectedBlocks.pageIndex!][
          state.selectedBlocks.blockIndices[0]
        ];

      if (inputFocused || block.blockType !== BLOCK_TYPE_KEYS.TEXT) return;

      const copyCommand = (e.ctrlKey || e.metaKey) && charCode === 'c';
      const pasteCommand = (e.ctrlKey || e.metaKey) && charCode === 'v';

      if (INVALID_KEYS.some((key) => e.key === key) || state.isTextBlockEditingEnabled || copyCommand || pasteCommand) {
        return;
      }

      const isFirefox = navigator.userAgent.match('Firefox');
      if (isFirefox) {
        let formattedValue = e.shiftKey ? charCode.toUpperCase() : charCode;
        let blockValue = block.value && typeof block.value === 'string' ? block.value : '';
        let finalValue = blockValue.concat(formattedValue);

        dispatch(
          actions.editBlock(
            state.selectedBlocks.documentId!,
            state.selectedBlocks.pageIndex!,
            state.selectedBlocks.blockIndices[0],
            { value: finalValue }
          )
        );
      }

      dispatch(actions.enableTextEditing());
    }

    document.addEventListener('keydown', handleKeyDown, false);
    return () => document.removeEventListener('keydown', handleKeyDown, false);
  }, [dispatch, state]);
}
